import { Message } from "@apis:message/message";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { Tab } from "@pages:app/talk/const";
import { useSearchActions } from "@stores:app/talk/search";
import { useMemo, useState } from "react";
import { MdDelete, MdOutlineMoreHoriz } from "react-icons/md";
import { useNavigate, useParams } from "react-router";

import { FeatureFlag } from "@:flag";

export const HistoryItem = ({
  message,
  hoveredConversationId,
  setHoveredConversationId,
  tab,
}: {
  message: Message["GET_ONE"]["Response"];
  hoveredConversationId: string | undefined;
  setHoveredConversationId: (value?: string) => void;
  tab?: Tab;
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const currentConvoId = useMemo(
    () => params.conversationId,
    [params.conversationId],
  );
  const searchActions = useSearchActions();

  const isConversationActive = currentConvoId === message.conversationId;
  const isConversationHovered =
    hoveredConversationId === message.conversationId;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  return (
    <Box
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        if ((e.target as any).classList.contains("MuiModal-backdrop")) {
          return;
        }

        if (tab === Tab.Chat) {
          if (currentConvoId === message?.conversationId) {
            return;
          }

          // set document title
          document.title = message.question;

          navigate(`/app/chat/${message?.conversationId}`);
        }

        if (tab === Tab.Search) {
          searchActions.resetAnswer();
          searchActions.setQuestion(message.question);
        }
      }}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderRadius: "5px",
        cursor: "pointer",
        paddingLeft: "14px",
        "&:hover, &.selected": {
          backgroundColor: "white",
        },
        backgroundColor: isConversationActive ? "white" : undefined,
      }}
      onMouseEnter={() =>
        setHoveredConversationId(message?.conversationId as string)
      }
      onMouseLeave={() => setHoveredConversationId(undefined)}
    >
      <Box
        sx={{
          display: "flex",
          paddingY: "0.7rem",
          gap: "8px",
          alignItems: "center",
        }}
      >
        <SVGInternalIcon
          icon="chatMessageIcon"
          sx={{
            width: "16px",
            height: "16px",
          }}
        />
        <Tooltip title={message.question} placement="bottom">
          <Typography
            sx={{
              fontSize: "14px",
              lineHeight: "20px",
              fontWeight: 400,
              letterSpacing: "0.15px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "calc(300px - 14px - 16px - 8px - 55px)",
            }}
          >
            {message.question}
          </Typography>
        </Tooltip>
      </Box>
      {(isConversationActive || isConversationHovered) &&
        FeatureFlag.DeleteMessageHistoryTalk && (
          <IconButton
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setAnchorEl(event.currentTarget);
            }}
            sx={{
              zIndex: 20,
            }}
          >
            <MdOutlineMoreHoriz />
          </IconButton>
        )}

      <Menu
        className="action-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          setAnchorEl(null);
          setHoveredConversationId(undefined);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 2,
              width: "123px",
              borderRadius: "15px",
            },
          },
        }}
      >
        <MenuItem
          id="action-delete"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
            event.nativeEvent.stopPropagation();

            if (tab === Tab.Chat) {
              navigate(`/app/chat/new`, { replace: true });
            }
            setAnchorEl(null);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            justifyContent: "center",
            color: "error.main",
          }}
        >
          <MdDelete />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};
